/**
 * Validation utilities for EntityAdapter implementations
 *
 * Sprint: sprint-5 (FR-P4-001)
 * Handles edge cases:
 * - Edge Case 1: Adapter returns invalid position format
 * - Edge Case 2: extractConnections() returns malformed edges
 * - Edge Case 3: Template creation when adapter doesn't support templates
 */

import type { Position } from '@modules/reactFlow/types/flow-canvas-types';
import type { Connection } from './EntityAdapter';

/**
 * Custom error for validation failures
 */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Custom error for unimplemented methods
 */
export class NotImplementedError extends Error {
  constructor(message: string = 'Method not implemented') {
    super(message);
    this.name = 'NotImplementedError';
  }
}

/**
 * Validates position format (Edge Case 1)
 *
 * @param position - Position to validate
 * @throws ValidationError if position is invalid
 */
export function validatePosition(position: any): asserts position is Position {
  if (typeof position !== 'object' || position === null) {
    throw new ValidationError('Invalid position format: position must be an object');
  }

  if (typeof position.x !== 'number' || typeof position.y !== 'number') {
    throw new ValidationError('Invalid position format: x and y must be numbers');
  }

  if (isNaN(position.x) || isNaN(position.y)) {
    throw new ValidationError('Invalid position format: x and y must not be NaN');
  }
}

/**
 * Filters valid connections, skipping malformed ones (Edge Case 2)
 *
 * @param connections - Array of connections to filter
 * @returns Filtered array with only valid connections
 */
export function filterValidConnections(connections: Connection[]): Connection[] {
  return connections.filter(conn => {
    // Check targetId
    if (!conn.targetId || conn.targetId === '') {
      console.warn('[EntityAdapter] Skipping malformed connection: targetId is null or empty', conn);
      return false;
    }

    // Check sourceActionKey
    if (!conn.sourceActionKey || conn.sourceActionKey === '') {
      console.warn('[EntityAdapter] Skipping malformed connection: sourceActionKey is null or empty', conn);
      return false;
    }

    return true;
  });
}

/**
 * Helper to create a NotImplementedError for unsupported createFromTemplate
 *
 * @param adapterName - Name of the adapter class
 * @returns NotImplementedError with descriptive message
 */
export function createTemplateNotSupportedError(adapterName: string): NotImplementedError {
  return new NotImplementedError(
    `Template creation not supported by ${adapterName}. Override createFromTemplate() to enable this feature.`
  );
}
